<template>
  <div class="h5-package-order-page">
    <!-- 头部 -->
    <div class="header">
      <div class="header-top">
        <div class="package-title">{{ packageInfo.name }}</div>
      </div>
      <div class="package-validity">
        ⏰ {{ t('validUntil') }}: {{ packageInfo.validEndDate }}
      </div>
      <div class="package-stats">
        <div class="stat-item">
          <span class="stat-icon">📦</span>
          <span>{{ packageInfo.productCount }} {{ t('products') }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-icon">🏪</span>
          <span>{{ packageInfo.customerCount }} {{ t('customers') }}</span>
        </div>
      </div>
    </div>

    <!-- 商品列表 -->
    <div class="products-list">
      <div v-for="product in products" :key="product.id" class="product-card">
        <div class="product-header">
          <div class="product-emoji">{{ product.emoji || '📦' }}</div>
          <div class="product-info">
            <div class="product-name">{{ product.name }}</div>
            <div class="product-name-en">{{ product.nameEn }}</div>
            <div class="price-section">
              <div class="wholesale-price">
                <span class="currency">S$</span>{{ (product.wholesalePrice || 0).toFixed(2) }}
              </div>
              <div class="market-price">S$ {{ (product.marketPrice || 0).toFixed(2) }}</div>
              <div v-if="product.savings" class="savings-badge">
                {{ t('save') }} S$ {{ product.savings.toFixed(2) }}
              </div>
            </div>
            <div class="product-unit">
              {{ t('minOrder') }}: {{ product.minOrderQuantity }} {{ product.unit }}
            </div>
          </div>
        </div>
        <div class="quantity-control">
          <button class="qty-btn" @click="decreaseQty(product)">−</button>
          <input
            v-model.number="product.quantity"
            type="number"
            :min="0"
            class="qty-input"
            @change="validateQty(product)"
          />
          <button class="qty-btn" @click="increaseQty(product)">+</button>
        </div>
      </div>

      <div v-if="products.length === 0" class="empty-state">
        <div class="empty-icon">📦</div>
        <p>{{ t('noProducts') }}</p>
      </div>
    </div>

    <!-- 底部购物车 -->
    <div class="cart-bar">
      <div class="cart-info">
        <div class="cart-label">{{ t('total') }}:</div>
        <div class="cart-amount">S$ {{ cartTotal.toFixed(2) }}</div>
      </div>
      <button class="checkout-btn" @click="checkout" :disabled="cartItems.length === 0">
        {{ t('checkout') }} ({{ cartItemCount }})
      </button>
    </div>

    <!-- 确认订单弹窗 -->
    <div v-if="showCheckout" class="modal-overlay" @click.self="showCheckout = false">
      <div class="modal checkout-modal">
        <div class="modal-header">
          <h2>{{ t('confirmOrder') }}</h2>
          <button class="close-btn" @click="showCheckout = false">×</button>
        </div>
        <div class="modal-body">
          <div class="order-summary">
            <div v-for="item in cartItems" :key="item.id" class="summary-item">
              <span>{{ item.name }} x {{ item.quantity }}</span>
              <span>S$ {{ (item.price * item.quantity).toFixed(2) }}</span>
            </div>
            <div class="summary-total">
              <span>{{ t('subtotal') }}:</span>
              <span>S$ {{ subtotal.toFixed(2) }}</span>
            </div>
            <div class="summary-total">
              <span>GST (9%):</span>
              <span>S$ {{ gst.toFixed(2) }}</span>
            </div>
            <div class="summary-total grand-total">
              <span>{{ t('total') }}:</span>
              <span>S$ {{ totalWithGst.toFixed(2) }}</span>
            </div>
          </div>
          <div class="form-group">
            <label>{{ t('deliveryMethod') }}</label>
            <select v-model="order.deliveryMethod" class="form-select">
              <option value="DELIVERY">{{ t('delivery') }}</option>
              <option value="SELF_PICKUP">{{ t('selfPickup') }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ t('paymentMethod') }}</label>
            <select v-model="order.paymentMethod" class="form-select">
              <option value="CASH">{{ t('cash') }}</option>
              <option value="PAYNOW">PayNow</option>
              <option value="BANK_TRANSFER">{{ t('bankTransfer') }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ t('remarks') }}</label>
            <textarea v-model="order.remark" class="form-textarea" :placeholder="t('remarksPlaceholder')"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn primary" @click="submitOrder" :disabled="submitting">
            {{ submitting ? t('submitting') : t('submit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'H5PackageOrder',
  data() {
    return {
      packageToken: this.$route.params.token,
      packageInfo: {
        name: '批发价专区',
        validEndDate: '',
        productCount: 0,
        customerCount: 0
      },
      products: [],
      showCheckout: false,
      submitting: false,
      order: {
        deliveryMethod: 'DELIVERY',
        paymentMethod: 'CASH',
        remark: ''
      }
    };
  },
  computed: {
    cartItems() {
      return this.products.filter(p => p.quantity && p.quantity > 0);
    },
    cartItemCount() {
      return this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    },
    cartTotal() {
      return this.cartItems.reduce((sum, item) => {
        return sum + ((item.wholesalePrice || 0) * (item.quantity || 0));
      }, 0);
    },
    subtotal() {
      return this.cartTotal;
    },
    gst() {
      return this.subtotal * 0.09;
    },
    totalWithGst() {
      return this.subtotal + this.gst;
    }
  },
  created() {
    this.fetchPackageInfo();
  },
  methods: {
    t(key) {
      const translations = {
        zh: {
          validUntil: '有效期至',
          products: '个商品',
          customers: '个客户',
          save: '省',
          minOrder: '最小起订',
          noProducts: '暂无商品',
          total: '总计',
          checkout: '去结算',
          confirmOrder: '确认订单',
          subtotal: '小计',
          deliveryMethod: '配送方式',
          delivery: '配送',
          selfPickup: '自提',
          paymentMethod: '支付方式',
          cash: '现金',
          bankTransfer: '银行转账',
          remarks: '备注',
          remarksPlaceholder: '请输入备注...',
          submit: '提交订单',
          submitting: '提交中...'
        },
        en: {
          validUntil: 'Valid until',
          products: 'products',
          customers: 'customers',
          save: 'Save',
          minOrder: 'Min order',
          noProducts: 'No products',
          total: 'Total',
          checkout: 'Checkout',
          confirmOrder: 'Confirm Order',
          subtotal: 'Subtotal',
          deliveryMethod: 'Delivery',
          delivery: 'Delivery',
          selfPickup: 'Self Pickup',
          paymentMethod: 'Payment',
          cash: 'Cash',
          bankTransfer: 'Bank Transfer',
          remarks: 'Remarks',
          remarksPlaceholder: 'Enter remarks...',
          submit: 'Submit',
          submitting: 'Submitting...'
        }
      };
      return translations[this.lang || 'zh'][key] || key;
    },
    async fetchPackageInfo() {
      try {
        const res = await api.get(`/customer/package/${this.packageToken}`);
        const data = res.data;
        this.packageInfo = {
          name: data.packageName || data.name,
          validEndDate: data.validEndDate ? data.validEndDate.split('T')[0] : '',
          productCount: data.products?.length || 0,
          customerCount: 0
        };
        this.products = (data.products || []).map(p => ({
          ...p,
          quantity: 0,
          savings: (p.marketPrice || 0) - (p.wholesalePrice || 0),
          emoji: this.getEmojiForCategory(p.categoryName)
        }));
      } catch (error) {
        console.error('Failed to fetch package info:', error);
        // 演示数据
        this.products = [
          { id: 1, name: '优质红富士苹果', nameEn: 'Premium Red Fuji Apple', marketPrice: 12.00, wholesalePrice: 10.00, unit: 'kg', minOrderQuantity: 10, quantity: 0, savings: 2.00, emoji: '🍎' },
          { id: 2, name: '新鲜橙子', nameEn: 'Fresh Orange', marketPrice: 8.50, wholesalePrice: 7.00, unit: 'kg', minOrderQuantity: 10, quantity: 0, savings: 1.50, emoji: '🍊' }
        ];
      }
    },
    getEmojiForCategory(categoryName) {
      const emojiMap = {
        '水果': '🍎', '蔬菜': '🥬', '肉类': '🥩', '海鲜': '🐟', '干货': '🌾'
      };
      return emojiMap[categoryName] || '📦';
    },
    increaseQty(product) {
      product.quantity = (product.quantity || 0) + 1;
    },
    decreaseQty(product) {
      if (product.quantity > 0) {
        product.quantity--;
      }
    },
    validateQty(product) {
      if (product.quantity < 0) product.quantity = 0;
    },
    checkout() {
      this.showCheckout = true;
    },
    async submitOrder() {
      this.submitting = true;
      try {
        const orderData = {
          orderType: 'SELF',
          customerId: 1, // TODO: 从登录信息获取
          items: this.cartItems.map(item => ({
            productId: item.id,
            quantity: item.quantity
          })),
          deliveryMethod: this.order.deliveryMethod,
          paymentMethod: this.order.paymentMethod,
          remark: this.order.remark
        };

        await api.post('/orders', orderData);
        this.$toast.success(this.t('submit') + '成功');
        this.showCheckout = false;
        // 清空购物车
        this.products.forEach(p => p.quantity = 0);
      } catch (error) {
        console.error('Failed to submit order:', error);
        this.$toast.error(this.t('submit') + '失败');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.h5-package-order-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.header {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #feada6 100%);
  color: white;
  padding: calc(16px + env(safe-area-inset-top, 0px));
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);

  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .package-title {
      font-size: 18px;
      font-weight: bold;
    }
  }

  .package-validity {
    font-size: 12px;
    opacity: 0.9;
    margin-top: 4px;
  }

  .package-stats {
    display: flex;
    gap: 16px;
    margin-top: 12px;

    .stat-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;

      .stat-icon {
        font-size: 16px;
      }
    }
  }
}

.products-list {
  padding: 12px;

  .product-card {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);

    .product-header {
      display: flex;
      gap: 12px;
      margin-bottom: 12px;

      .product-emoji {
        font-size: 48px;
        width: 70px;
        height: 70px;
        background: #f5f7fa;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .product-info {
        flex: 1;

        .product-name {
          font-size: 16px;
          font-weight: 500;
          margin-bottom: 4px;
          color: #333;
        }

        .product-name-en {
          font-size: 12px;
          color: #999;
          margin-bottom: 8px;
        }

        .price-section {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;

          .wholesale-price {
            font-size: 22px;
            font-weight: bold;
            color: #ff6b6b;

            .currency {
              font-size: 14px;
            }
          }

          .market-price {
            font-size: 14px;
            color: #999;
            text-decoration: line-through;
          }

          .savings-badge {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
          }
        }

        .product-unit {
          font-size: 12px;
          color: #999;
          margin-bottom: 12px;
        }
      }
    }

    .quantity-control {
      display: flex;
      align-items: center;
      gap: 12px;

      .qty-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid #e0e0e0;
        background: white;
        font-size: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;

        &:active {
          background: #f5f5f5;
        }
      }

      .qty-input {
        width: 60px;
        text-align: center;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 8px;
        font-size: 15px;
        font-weight: 500;
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;

  .empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
  }
}

.cart-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;

  .cart-info {
    .cart-label {
      font-size: 12px;
      color: #999;
    }

    .cart-amount {
      font-size: 24px;
      font-weight: bold;
      color: #ff6b6b;
    }
  }

  .checkout-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 12px 32px;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;

    h2 {
      font-size: 18px;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 28px;
      cursor: pointer;
      color: #999;
    }
  }

  .modal-body {
    padding: 20px;

    .order-summary {
      margin-bottom: 20px;

      .summary-item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        font-size: 14px;
        border-bottom: 1px solid #f0f0f0;
      }

      .summary-total {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        font-size: 14px;

        &.grand-total {
          font-size: 16px;
          font-weight: bold;
          color: #667eea;
          border-top: 2px solid #667eea;
          margin-top: 8px;
          padding-top: 12px;
        }
      }
    }

    .form-group {
      margin-bottom: 16px;

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
      }

      .form-select {
        width: 100%;
        padding: 12px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        font-size: 14px;
      }

      .form-textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        font-size: 14px;
        resize: vertical;
        min-height: 80px;
      }
    }
  }

  .modal-footer {
    padding: 20px;
    border-top: 1px solid #e0e0e0;

    .modal-btn {
      width: 100%;
      padding: 14px;
      border: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;

      &.primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
      }
    }
  }
}
</style>
